import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable, forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { RouterLink } from '@angular/router';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService, hrErrorMessage } from '../hr.service';
import { CommonService } from '@app/common/common.service';
import { ValuesService } from '@app/common/values/values.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { C_value, Store, User } from '@app/shared/_models/common';
import {
	HrAbsence, HrCounter, HrEmployee, HrLock, hrEmployeeNameOf, hrIsHrManager, hrMonthLabel, hrPeriodLabel,
} from '@app/shared/_models/hr';
import {
	C_VALUES_HR_ABSENCE_TYPE, C_VALUES_HR_PAYROLL_WEEKS,
	EVENTTYP_DELETE, EVENTTYP_INSERT, HR_ABSENCE_STATE_APPROVED,
	HR_COUNTER_KIND_PAID, HR_OVERTIME_ALERT_MIN, OBJECTTYP_HR_COUNTER, OBJECTTYP_HR_LOCK,
} from '@app/shared/constants';
import {
	AbsenceTotal, DayValidation, OvertimeEntry, PayrollPremiumColumn, PayrollRow, PayrollRules,
	PayrollTierColumn, PayrollWeekLine,
	buildPayroll, buildPayrollCsv, hrPayrollRulesOf, payrollPremiumMinutes, payrollPremiumsOf, payrollTiersOf,
} from '../hr-payroll';
import { euros, round2 } from '../hr-meal-voucher';
import { corAbsenceTypes, rcrCostOfRequest, rcrLabel } from '../hr-rcr';
import {
	hrAbsenceAccrualLabel, hrAbsencePayLabel, hrAbsenceProfileLabel, hrAbsenceWorkTimeLabel,
} from '../hr-absence-catalog';
import { LeaveRules, leaveRulesOf } from '../hr-leave-rights';
import {
	PayrollIssue, PayrollIssueLevel, payrollIssueCounts, payrollIssueSummary, payrollIssues,
} from '../hr-payroll-checks';
import { EffectiveSlot, addDays, deltaLabel, effectiveSlotsForRange, frenchHolidaysOf, hoursLabel, mondayOf } from '../hr-schedule-engine';
import { UnclosedDay, WorkPeriod, pairClocks, unclosedDays } from '../hr-timesheet';
import {
	PAYROLL_MAX_CARRY_WEEKS,
	PayrollOvertimeArbitration, PayrollWeekChoice, PayrollWeekClosure, arbitratedMinutes, closureOf,
	forgottenWeeks, payrollPeriodOf, payrollTierKey, payrollWeekChoices,
	restDecisionMap, retainedMondays, spreadOvertime, weeksSummary, withArbitration, withoutEmployee,
} from '../hr-payroll-weeks';
import { nowIso, todayStr } from '@app/shared/time';

// Rapport de préparation de la paie — /hr/report.
// Une période (le mois par défaut), une ligne par salarié : heures contractuelles, planifiées,
// payées, majorations, absences ventilées, titres-restaurant. C'est ce qu'on exporte au comptable.
//
// Le rapport ne CALCULE rien lui-même : tout vient du moteur pur hr-payroll.ts. Ici on charge,
// on assemble, on affiche, on exporte — et on verrouille la période une fois la paie faite.
@Component({
	selector: 'app-hr-report',
	standalone: true,
	imports: [RouterLink, MenuCommonComponent, ReactiveFormsModule, MatFormFieldModule, MatSelectModule,
		MatButtonModule, MatIconModule, MatTooltipModule, MatCheckboxModule],
	templateUrl: './report.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './report.component.scss',
})
export class ReportComponent implements OnInit {

	public hrService = inject(HrService);
	private commonService = inject(CommonService);
	private valuesService = inject(ValuesService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);

	public from = '';
	public to = '';
	public periodLabel = '';			// « Juillet 2026 » — ce qu'on lit en haut de page
	public periodRange = '';				// « 01/07/2026 → 31/07/2026 » — pour les confirmations
	public weeksLabel = '';
	public rows: PayrollRow[] = [];
	public absenceTypes: C_value[] = [];
	public rules: PayrollRules = hrPayrollRulesOf(null);
	// Congés payés : l'unité de décompte et le rythme d'acquisition de l'établissement (mêmes
	// réglages c_values 25, sous-objet `leave`). Le tableau des absences en annonce les chiffres.
	public leaveRules: LeaveRules = leaveRulesOf(null);
	public stores: Store[] = [];
	public storeCtrl = new FormControl<number | null>(null);
	private storeDefaulted = false;			// le filtre par défaut ne s'impose qu'au premier chargement
	public loading = false;
	public isAdmin = false;

	// Colonnes optionnelles : masquées quand la colonne est vide partout (un magasin qui ne
	// travaille jamais la nuit n'a pas besoin d'une colonne « nuit »).
	// Conformité légale : récupération, contingent annuel, congés payés dans l'assiette.
	// `showRest` = la période porte du temps à récupérer ; `showRestEdit` (plus bas) = la colonne
	// est offerte à la saisie, ce qui reste vrai même à zéro tant que l'établissement l'autorise.
	public showRest = false;
	// Ajustement du manager : les heures majorées RETENUES pour la paie du mois, par salarié.
	// La décision est stockée dans hr_counter (kind 3 = heures sup payées, elles sortent du stock).
	private paidAdjust = new Map<number, HrCounter>();

	// ARBITRAGE DES HEURES MAJORÉES — une saisie par (salarié, semaine, taux). Le moteur propose
	// une ventilation légale, le manager déplace les heures d'un taux à l'autre, et « Heures sup
	// payées » n'est plus une saisie mais la SOMME de ces colonnes : le total ne peut plus
	// contredire son propre détail.
	public arbitration: PayrollOvertimeArbitration = {};
	private tierCtrls = new Map<string, FormControl<number | null>>();

	// MISE EN RÉCUPÉRATION — la décision « ces heures-là, on ne les paie pas, elles se récupèrent ».
	// Même maille que l'arbitrage des taux (salarié × semaine × palier), même stockage, et le même
	// geste : on saisit des heures, on valide, le total payé suit. Ce que le salarié récupère sort
	// du montant à payer et entre dans son compteur d'heures à récupérer, majoration comprise.
	public restArbitration: PayrollOvertimeArbitration = {};
	private restCtrls = new Map<string, FormControl<number | null>>();
	// Colonne « À récupérer » : visible dès qu'il y a un stock à montrer, ou dès que la politique
	// de l'établissement permet d'en créer — sinon on ne pourrait jamais y poser la première heure.
	public showRestEdit = false;

	// Deux lectures du même mois : le récapitulatif, et le décompte SEMAINE PAR SEMAINE — les
	// heures supplémentaires naissent dans la semaine, c'est donc là qu'on doit pouvoir les
	// reprendre.
	public view: 'month' | 'week' = 'month';
	public showContingent = false;
	public showAssimilated = false;
	public showBadged = false;			// sans badgeuse, la colonne ne contiendrait que des zéros
	// « Réalisé » est la colonne qui compte — le temps de travail retenu, celui qui part en paie.
	// « Planifié » ne dit quelque chose que s'il S'EN ÉCARTE : sans pointeuse et sans journée
	// reprise à la main, les deux colonnes portent le même chiffre, et le lecteur cherche une
	// différence qui n'existe pas.
	public showPlanned = false;
	public showVouchers = false;

	public locks: HrLock[] = [];
	public lockedDays = 0;
	public unlockedDays = 0;

	// L9 — points à vérifier avant l'export (règles déterministes, aucune IA).
	public issues: PayrollIssue[] = [];
	public issueSummary = '';
	public issueCounts: Record<PayrollIssueLevel, number> = { blocking: 0, warning: 0, info: 0 };
	// Journées passées jamais closes (mode planning) : nourrit le bandeau des contrôles ET le
	// verrouillage — le verrou de paie enregistre ce reste-à-faire (hr_lock.data.unclosed_days).
	public unclosed: UnclosedDay[] = [];
	public issuesOpen = false;
	public validatedDays = 0;			// journées dont le temps rémunéré a été arbitré (L8)

	// SEMAINES COMPTÉES par cette paie — le point le plus délicat du rapport, cf. hr-payroll-weeks.ts.
	public closures: PayrollWeekClosure[] = [];
	public weekChoices: PayrollWeekChoice[] = [];
	public savingWeeks = false;

	private employees: HrEmployee[] = [];
	private users: User[] = [];

	ngOnInit(): void {
		this.storeCtrl.valueChanges.subscribe(() => this.load());
		this.setMonth(todayStr());
	}

	// ============================================================
	// Période
	// ============================================================

	private setMonth(dat: string): void {
		const first = dat.slice(0, 8) + '01';
		const nextMonth = addDays(first, 32).slice(0, 8) + '01';
		this.from = first;
		this.to = addDays(nextMonth, -1);
		this.load();
	}

	onPrevMonth(): void {
		this.setMonth(addDays(this.from, -1));
	}

	onNextMonth(): void {
		this.setMonth(addDays(this.to, 1));
	}

	onToday(): void {
		this.setMonth(todayStr());
	}

	// ============================================================
	// Chargement
	// ============================================================

	private load(): void {
		if (!this.from || !this.to) return;
		this.loading = true;

		// Les créneaux se projettent sur les semaines ENTIÈRES qui touchent la période : les
		// heures sup se comptent par semaine ISO, il faut donc les bords.
		const projFrom = mondayOf(this.from);
		const projTo = addDays(mondayOf(this.to), 6);
		// E4 — une paie peut REPRENDRE une semaine oubliée jusqu'à 8 semaines en arrière
		// (hr-payroll-weeks.ts) : créneaux, pointages, validations et absences se chargent dès le
		// lundi le plus ancien susceptible d'être retenu. Sans quoi la semaine rattrapée arrivait
		// sans ses heures : +35 h de contrat, 0 h travaillée, et des heures supplémentaires —
		// qui naissent dans la semaine (L3121-28) — jamais calculées.
		const carryFrom = addDays(projFrom, -7 * PAYROLL_MAX_CARRY_WEEKS);
		// E5 — le contingent d'heures supplémentaires se compte sur l'ANNÉE CIVILE (L3121-30) : on
		// remonte au 1ᵉʳ janvier pour recalculer ce qui a déjà été consommé. Le volume reste
		// raisonnable : au pire douze mois de créneaux et d'absences pour l'effectif du magasin.
		const yearStart = this.from.slice(0, 4) + '-01-01';
		const loadFrom = mondayOf(yearStart) < carryFrom ? mondayOf(yearStart) : carryFrom;

		forkJoin({
			employees: this.hrService.getEmployees$(),
			users: this.hrService.getUsers$(),
			stores: this.commonService.getStores(),
			schedules: this.hrService.getSchedules$(),
			absences: this.hrService.getAbsences$(loadFrom, projTo),
			overrides: this.hrService.getOverrides$(loadFrom, projTo),
			clocks: this.hrService.getClocks$(loadFrom, projTo),
			locks: this.hrService.getLocks$(this.from, this.to),
			validations: this.hrService.getValidations$(loadFrom, projTo),
			documents: this.hrService.getDocuments$(),
			absenceTypes: this.hrService.valuesOf$(C_VALUES_HR_ABSENCE_TYPE),
			payrollRules: this.hrService.payrollRules$(),
			// Semaines arrêtées par les paies précédentes : c'est ce qui dit où reprendre, et ce
			// qu'il ne faut surtout pas compter deux fois.
			payrollWeeks: this.hrService.payrollWeeks$(),
			// Ajustements du compteur de la période : kind 3 = heures majorées payées, c'est là que
			// vit la valeur retenue par le manager.
			counters: this.hrService.getCounters$(this.from, this.to),
			// Repas payés par l'entreprise : ils retirent le titre-restaurant de leur jour. Sans eux,
			// la colonne « Titres-restaurant » compterait des titres que l'entreprise ne doit pas.
			meals: this.hrService.getMeals$(this.from, this.to),
		}).subscribe(({ employees, users, stores, schedules, absences, overrides, clocks, locks, validations, documents, absenceTypes, payrollRules, payrollWeeks, counters, meals }) => {
			this.closures = payrollWeeks;
			this.users = users;
			this.stores = stores;
			this.isAdmin = hrIsHrManager(this.loginService.loggeduser, users);
			this.absenceTypes = absenceTypes;
			this.locks = locks;
			// Règles déjà parsées par HrService (le référentiel 25 ne porte qu'UN élément).
			this.rules = payrollRules.rules;
			this.leaveRules = payrollRules.leave;

			// L'établissement du poste sert de filtre par défaut — UNE SEULE FOIS. Le refaire à
			// chaque chargement rendait « Tous » impossible à choisir : la sélection repartait
			// aussitôt sur le magasin courant.
			if (!this.storeDefaulted) {
				this.storeDefaulted = true;
				this.storeCtrl.setValue(this.loginService.currentstation?.store ?? null, { emitEvent: false });
			}
			const store = this.storeCtrl.value;
			this.employees = employees.filter(e => e.is_active !== false
				&& (store == null || e.id_store === store || (e.mobility ?? []).includes(store)));

			// L'année précédente aussi : le chargement remonte jusqu'en décembre (semaine de la
			// 1ʳᵉ semaine ISO de janvier, semaines reprises d'un début d'année).
			const holidays = new Map([
				...frenchHolidaysOf(+this.from.slice(0, 4) - 1),
				...frenchHolidaysOf(+this.from.slice(0, 4)),
				...frenchHolidaysOf(+this.to.slice(0, 4)),
			]);
			// La projection couvre TOUTE la plage chargée : le mois affiché, les semaines reprises
			// possibles (E4) et le début d'année pour le contingent (E5).
			const slots = effectiveSlotsForRange(loadFrom, projTo, schedules, overrides, absences as HrAbsence[]);
			const { periods, anomalies } = pairClocks(clocks, todayStr());

			// Journées passées de la période jamais closes : détectées ICI (une seule fois) pour le
			// bandeau des contrôles et pour le verrouillage. Vide par construction en mode pointeuse.
			this.unclosed = unclosedDays(this.employees.map(e => e.id), slots, validations,
				this.from, this.to, todayStr(), payrollRules.rules.time_source);

			const names = new Map(this.employees.map(e => [e.id, hrEmployeeNameOf(e, users)]));
			const comments = new Map(this.employees.map(e => [e.id, (e.data?.pay_comments?.[this.from.slice(0, 7)] ?? '') as string]));

			// Les SEMAINES comptées par cette paie : le choix arrêté s'il existe, la règle du jeudi
			// en proposition sinon. C'est lui qui décide où s'arrêtent les heures supplémentaires.
			this.weekChoices = payrollWeekChoices(this.from, this.to, this.closures, store);

			// L'arbitrage du mois, relu depuis l'arrêté. Il doit être connu AVANT le calcul : c'est
			// lui qui dit quelles heures partent en récupération plutôt qu'en paie.
			this.arbitration = this.closureFor(store)?.overtime ?? {};
			this.restArbitration = this.closureFor(store)?.rest ?? {};

			// CONTREPARTIE OBLIGATOIRE EN REPOS DÉJÀ PRISE : les absences d'un type marqué « COR »
			// (c_values 23, data.cor). Sans elles, l'écran réclamerait un repos déjà soldé.
			// Bornée à l'ANNÉE CIVILE, comme la dette qu'elle solde : la COR naît du franchissement
			// du contingent annuel, et se prend — donc se décompte — sur la même année.
			const corTaken = this.corTakenOf(
				(absences as HrAbsence[]).filter(a => a.dateend >= yearStart), slots);

			// `absent` voyage avec la validation : une journée validée absente n'ouvre aucun droit
			// à titre-restaurant, même si le planning prévoyait toujours ses créneaux.
			const dayVals: DayValidation[] = validations
				.map(v => ({ id_employee: v.id_employee, dat: v.dat, paid_min: v.paid_min, absent: v.absent }));

			// E5 — CONTINGENT ANNUEL : les heures supplémentaires déjà nées depuis le 1ᵉʳ janvier,
			// recalculées par le MÊME moteur semaine par semaine, puis expurgées des semaines que
			// cette paie compte — chaque semaine ne s'impute qu'UNE fois sur le contingent.
			const yearEntries = this.yearEntriesOf(names, slots, periods, absences as HrAbsence[], dayVals, holidays);

			// L8 — une journée validée impose son temps rémunéré ; sinon la cascade
			// badgé → planifié s'applique (cf. hr-payroll.ts).
			this.rows = buildPayroll(
				this.employees, names, this.rules, slots, periods, absences as HrAbsence[],
				this.absenceTypes, holidays, this.from, this.to,
				dayVals,
				comments, [], yearEntries, retainedMondays(this.weekChoices),
				restDecisionMap(this.restArbitration), corTaken, meals,
			);
			this.validatedDays = validations.filter(v => v.validated && v.dat >= this.from && v.dat <= this.to).length;

			// Heures majorées RETENUES par le manager : il module la proposition du moteur, et la
			// décision vit dans hr_counter (kind 3 « heures sup payées », qui sort du stock d'heures).
			this.paidAdjust = new Map(counters
				.filter(c => c.kind === HR_COUNTER_KIND_PAID && c.dat >= this.from && c.dat <= this.to)
				.map(c => [c.id_employee, c]));

			// Les saisies sont reconstruites À CHAQUE chargement : une case qui garderait une valeur
			// non enregistrée mentirait.
			this.tierCtrls.clear();
			this.restCtrls.clear();

			this.showBadged = this.rules.time_source === 'clock';
			this.showPlanned = this.showBadged || this.rows.some(r => r.paid_min !== r.planned_min);
			// Colonnes de conformité : elles n'apparaissent que si elles ont quelque chose à dire.
			this.showRest = this.rows.some(r => r.rest_total_min > 0);
			// La saisie de mise en récupération reste ouverte tant que l'établissement l'autorise :
			// une colonne qui n'apparaît qu'une fois remplie ne peut jamais être remplie.
			this.showRestEdit = this.rules.rcr_enabled || this.showRest;
			this.showContingent = this.rows.some(r => r.contingent.used_min > 0);
			this.showAssimilated = this.rows.some(r => r.assimilated_min > 0);
			this.showVouchers = this.rows.some(r => r.meal_vouchers > 0);
			this.computeLockState();

			// L9 — les points à vérifier AVANT d'exporter au comptable.
			this.issues = payrollIssues({
				rows: this.rows,
				source: this.rules.time_source,
				employees: this.employees,
				absences: absences as HrAbsence[],
				absenceTypes: this.absenceTypes,
				anomalies,
				unclosed: this.unclosed,
				// L5 — un document rattaché à une absence (data.hr.id_absence) vaut justificatif.
				documentsByAbsence: new Set<number>(
					documents.map(d => d.data?.hr?.id_absence).filter((id: any) => typeof id === 'number')),
				from: this.from, to: this.to,
				fullyLocked: this.fullyLocked,
				overtimeAlertMin: HR_OVERTIME_ALERT_MIN,
			});
			this.issueSummary = payrollIssueSummary(this.issues);
			this.issueCounts = payrollIssueCounts(this.issues);
			this.periodLabel = hrMonthLabel(this.from);
			this.periodRange = hrPeriodLabel(this.from, this.to);
			this.weeksLabel = this.buildWeeksLabel();
			this.loading = false;
		});
	}

	// « S27 · S28 · S29 · S30 » — les semaines RÉELLEMENT comptées, pas l'étendue du mois.
	private buildWeeksLabel(): string {
		return weeksSummary(this.weekChoices);
	}

	// ============================================================
	// Contingent annuel — les heures sup déjà faites depuis le 1ᵉʳ janvier (E5)
	// ============================================================
	//
	// Le contingent se compte sur l'ANNÉE CIVILE (L3121-30), pas sur le mois affiché : sans ce
	// rappel, le « contingent consommé » repartait de zéro à chaque rapport et la contrepartie
	// obligatoire en repos ne pouvait jamais se déclencher à l'écran. On rejoue les semaines du
	// 1ᵉʳ janvier à la veille de la période avec buildPayroll lui-même — même cascade de temps
	// retenu (validé → badgé → planifié selon le mode de décompte), mêmes règles — et on en tire
	// les arbitrages semaine par semaine que le calcul du mois vient additionner aux siens.
	private yearEntriesOf(
		names: Map<number, string>,
		slots: EffectiveSlot[],
		periods: WorkPeriod[],
		absences: HrAbsence[],
		validations: DayValidation[],
		holidays: Map<string, string>,
	): OvertimeEntry[] {
		const yearStart = this.from.slice(0, 4) + '-01-01';
		const prefixTo = addDays(this.from, -1);
		if (prefixTo < yearStart) return [];			// janvier : rien à rappeler avant le 1ᵉʳ janvier

		// Les semaines comptées par CETTE paie sortent du rappel : leurs heures arrivent par les
		// entrées du mois, et une semaine comptée deux fois gonflerait le contingent d'autant.
		const counted = new Set(retainedMondays(this.weekChoices));
		// Les mises en récupération décidées les mois précédents (arrêtés de paie, c_values 29)
		// sortent leurs heures du contingent dès l'arbitrage (L3121-30 al. 2) : on rejoue ces
		// décisions, toutes périodes confondues — la clé porte déjà le salarié et le lundi.
		const rest = new Map<string, number>();
		for (const c of this.closures) {
			for (const [k, v] of restDecisionMap(c.rest)) rest.set(k, v);
		}

		return buildPayroll(
			this.employees, names, this.rules, slots, periods, absences, this.absenceTypes,
			holidays, yearStart, prefixTo, validations, new Map(), [], [], null, rest,
		).flatMap(r => r.entries).filter(e => !counted.has(e.monday));
	}

	// ============================================================
	// Semaines comptées — l'arrêté de la période
	// ============================================================
	//
	// Les heures supplémentaires se comptent par SEMAINE ENTIÈRE. Une semaine à cheval sur deux
	// mois appartient à l'une des deux paies, jamais aux deux, jamais à aucune : c'est un choix
	// humain, et il doit être ENREGISTRÉ pour que le mois suivant sache où reprendre.

	// Les semaines laissées derrière : antérieures, jamais comptées, et pas reprises ici non plus.
	get forgottenWeeks(): PayrollWeekChoice[] {
		return forgottenWeeks(this.weekChoices);
	}

	// L'arrêté de CETTE période — il porte les semaines comptées ET l'arbitrage des heures majorées.
	private closureFor(store: number | null): PayrollWeekClosure | undefined {
		return this.closures.find(c => c.period === payrollPeriodOf(this.from)
			&& (c.id_store == null || c.id_store === store));
	}

	// La période a-t-elle été arrêtée explicitement, ou lit-on la proposition automatique ?
	// L'élément existe dès qu'on y range un arbitrage d'heures : seule sa DATE d'arrêté fait foi.
	get weeksClosed(): boolean {
		return this.closureFor(this.storeCtrl.value)?.closed_at != null;
	}

	weekTooltip(choice: PayrollWeekChoice): string {
		if (choice.taken_by) return 'Déjà comptée sur la paie de ' + choice.taken_by + ' — elle ne peut pas l\'être deux fois';
		if (choice.origin === 'carried') return 'Semaine d\'un mois précédent, jamais comptée : à reprendre ici';
		if (choice.origin === 'ahead') return 'Semaine à cheval sur le mois suivant : à vous de décider si elle part sur cette paie';
		return 'Semaine du mois';
	}

	// Cocher / décocher ENREGISTRE : un arrêté qui ne survit pas au rechargement ne vaut rien, et
	// c'est justement ce que le mois suivant doit pouvoir lire.
	onToggleWeek(choice: PayrollWeekChoice): void {
		if (!this.isAdmin || choice.taken_by || this.savingWeeks) return;
		choice.included = !choice.included;
		this.saveWeeks();
	}

	// Arrête la période sur la proposition affichée — le geste « c'est bon, on part là-dessus ».
	onCloseWeeks(): void {
		if (!this.isAdmin || this.savingWeeks) return;
		this.saveWeeks();
	}

	private saveWeeks(): void {
		this.writeClosure(nowIso(), this.arbitration, 'Semaines comptées : ' + weeksSummary(this.weekChoices));
	}

	// Une seule écriture pour les deux décisions du mois — les semaines comptées et la répartition
	// des heures majorées vivent dans le MÊME élément, et se réécrivent donc ensemble.
	//
	// `closedAt` à `null` enregistre sans ARRÊTER : c'est ce qui permet de répartir des heures dans
	// une période encore ouverte sans confisquer ses semaines à la paie suivante.
	private writeClosure$(closedAt: string | null, overtime: PayrollOvertimeArbitration): Observable<any> {
		const period = payrollPeriodOf(this.from);
		const store = this.storeCtrl.value ?? null;
		// Les deux décisions du mois voyagent ensemble : ce qui est payé à quel taux, et ce qui part
		// en récupération. Les séparer laisserait l'une écraser l'autre à chaque enregistrement.
		const closure = closureOf(period, store, this.weekChoices,
			this.loginService.loggeduser?.id ?? null, closedAt, overtime, this.restArbitration);

		// Le référentiel s'écrit en BLOC : on repose toutes les autres clôtures telles quelles et
		// on remplace la nôtre — celle du même (établissement, mois).
		const others = this.closures.filter(c => !(c.period === period && (c.id_store ?? null) === store));
		const elements = [...others, closure].map((c, i) => ({
			id: i + 1, seq: i + 1,
			name: c.period + (c.id_store != null ? ' — établissement ' + c.id_store : ''),
			is_active: true,
			data: c,
		}));
		return this.hrService.postValuesElements(C_VALUES_HR_PAYROLL_WEEKS, elements, 'Semaines de paie');
	}

	private writeClosure(closedAt: string | null, overtime: PayrollOvertimeArbitration, message: string,
		then$: Observable<any> = of(null)): void {
		this.savingWeeks = true;
		this.writeClosure$(closedAt, overtime).pipe(switchMap(() => then$)).subscribe({
			next: () => {
				this.savingWeeks = false;
				this.valuesService.updateData();
				this.alertService.success(message);
				this.load();
			},
			error: (e) => {
				this.savingWeeks = false;
				this.alertService.error(hrErrorMessage(e));
				this.load();			// on ne garde pas à l'écran un arrêté qui n'a pas été écrit
			},
		});
	}

	// ============================================================
	// Verrouillage de la période (L0.3)
	// ============================================================

	// Le verrou porte sur un ÉTABLISSEMENT (id_store), ou sur TOUTE l'organisation quand il est
	// posé sans magasin (id_store null). Il n'existe pas de verrou par poste ni par salarié :
	// verrouiller, c'est fermer un intervalle de dates à toute écriture, pour tout le monde
	// dans le périmètre choisi. Le libellé du bouton dit lequel, pour qu'on ne le devine pas.
	get lockScopeLabel(): string {
		const store = this.storeCtrl.value;
		if (store == null) return 'tous les établissements';
		return this.stores.find(s => s.id === store)?.name ?? 'cet établissement';
	}

	// Un jour compte comme verrouillé POUR LA VUE COURANTE. Sur un établissement : dès qu'un
	// verrou le couvre. Sur « Tous » : seulement si plus rien n'est modifiable nulle part — un
	// verrou d'organisation, ou un verrou sur chacun des établissements. Sinon la page
	// annoncerait « période verrouillée » alors qu'un magasin reste ouvert à l'écriture.
	private isDayLocked(dat: string, store: number | null): boolean {
		if (store != null) return this.locks.some(l => HrLock.covers(l, dat, store));
		if (this.locks.some(l => l.id_store == null && HrLock.covers(l, dat, null))) return true;
		return this.stores.length > 0
			&& this.stores.every(s => this.locks.some(l => HrLock.covers(l, dat, s.id)));
	}

	private computeLockState(): void {
		const store = this.storeCtrl.value;
		let locked = 0, total = 0;
		for (let d = this.from; d <= this.to; d = addDays(d, 1)) {
			total++;
			if (this.isDayLocked(d, store)) locked++;
		}
		this.lockedDays = locked;
		this.unlockedDays = total - locked;
	}

	get fullyLocked(): boolean {
		return this.unlockedDays === 0 && this.lockedDays > 0;
	}

	onLockPeriod(): void {
		// Pendant un rechargement, from/to et le compte de journées non closes appartiennent
		// encore à l'ANCIENNE période : confirmer maintenant poserait un verrou décalé.
		if (this.loading) return;
		// Verrouiller avec des journées non closes n'est pas interdit (la paie doit pouvoir partir),
		// mais ça se dit AVANT et ça s'ENREGISTRE : le verrou emporte ce reste-à-faire dans sa photo.
		// À la portée du VERROU près : un verrou de magasin ne bloque que les salariés DE ce magasin
		// (locked_period lit hr_employee.id_store) — les mobiles venus d'ailleurs n'en sont pas, et
		// les compter ici ferait archiver le même reste-à-faire dans deux verrous.
		const store = this.storeCtrl.value;
		const unclosedCount = this.unclosed.filter(u =>
			store == null || this.employees.find(e => e.id === u.id_employee)?.id_store === store).length;
		Swal.fire({
			title: 'Verrouiller ' + this.periodLabel + ' ?',
			html: 'Période : <b>' + this.periodRange + '</b><br>Portée : <b>' + this.lockScopeLabel + '</b>'
				+ (unclosedCount
					? '<br><br><b>⚠ ' + unclosedCount + ' journée' + (unclosedCount > 1 ? 's' : '')
						+ ' non close' + (unclosedCount > 1 ? 's' : '') + '</b> : '
						+ (unclosedCount > 1 ? 'elles partiront' : 'elle partira')
						+ ' en paie sur l\'horaire planifié — ou sur la déclaration du salarié quand elle '
						+ 'existe — sans arbitrage enregistré.'
					: '')
				+ '<br><br>Plus aucune modification de planning, d\'absence ni de pointage ne sera possible '
				+ 'sur cette période, pour tous les salariés concernés.<br>Le déverrouillage reste possible et tracé.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Verrouiller',
			cancelButtonText: 'Annuler',
		}).then(res => {
			if (!res.isConfirmed) return;
			const payload = this.hrService.buildLockPayload({
				id: 0, id_store: this.storeCtrl.value, datstart: this.from, datend: this.to,
				comment: 'Paie ' + this.from.slice(0, 7),
				// En mode planning, le verrou dit AUSSI avec quel reste-à-faire il a été posé
				// (0 = tout était clos). En mode pointeuse la notion n'existe pas : null.
				unclosed_days: this.rules.time_source === 'clock' ? null : unclosedCount,
			});
			this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_LOCK, payload).subscribe({
				next: () => {
					this.alertService.success('Période verrouillée');
					this.load();
				},
				// Refus métier (rôle non responsable RH… — converti en erreur par postHrEvent)
				// comme vraie panne : le message du serveur s'affiche tel quel.
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}

	onUnlockPeriod(): void {
		const store = this.storeCtrl.value;
		const mine = this.locks.filter(l => l.datstart <= this.to && l.datend >= this.from
			&& (l.id_store == null || store == null || l.id_store === store));
		if (!mine.length) return;

		Swal.fire({
			title: 'Déverrouiller ' + this.periodLabel + ' ?',
			html: 'Portée : <b>' + this.lockScopeLabel + '</b> — ' + mine.length + ' verrou'
				+ (mine.length > 1 ? 's seront levés' : ' sera levé') + '.'
				+ '<br>La période redeviendra modifiable. L\'opération est tracée.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Déverrouiller',
			cancelButtonText: 'Annuler',
		}).then(res => {
			if (!res.isConfirmed) return;
			// Un seul event par verrou : la liste est courte (un par mois au plus).
			let remaining = mine.length;
			for (const lock of mine) {
				this.hrService.postHrEvent(EVENTTYP_DELETE, OBJECTTYP_HR_LOCK, { id: lock.id }).subscribe({
					next: () => { if (--remaining === 0) { this.alertService.success('Période déverrouillée'); this.load(); } },
					error: (e) => this.alertService.error(hrErrorMessage(e)),
				});
			}
		});
	}

	// ============================================================
	// Affichage
	// ============================================================

	hours(min: number): string {
		return hoursLabel(min);
	}

	// D'où viennent les heures de ce rapport : le comptable doit le lire sans avoir à demander.
	get sourceLabel(): string {
		return this.rules.time_source === 'clock'
			? 'Heures comptées par la pointeuse'
			: 'Heures comptées par le planning';
	}

	get sourceIcon(): string {
		return this.rules.time_source === 'clock' ? 'fingerprint' : 'event_note';
	}

	// « 35h × 5 » — le calcul du volume contractuel de la période, écrit devant son résultat. Un
	// contrat se lit en heures par SEMAINE : sans le détail, « 175h » ne se vérifie pas d'un coup
	// d'œil, et personne ne sait si la paie compte 4 ou 5 semaines.
	contractDetail(row: PayrollRow): string {
		if (row.contract_week_min <= 0 || row.contract_weeks <= 0) return '';
		return hoursLabel(row.contract_week_min) + ' × ' + row.contract_weeks;
	}

	deltaLabel(row: PayrollRow): string {
		return deltaLabel(row.delta_min);
	}

	deltaClass(row: PayrollRow): string {
		if (row.delta_min === 0) return '';
		return row.delta_min > 0 ? 'over' : 'under';
	}

	overtimeAt(row: PayrollRow, tier: PayrollTierColumn): number {
		return row.overtime.find(b => b.rate === tier.rate && b.tier_kind === tier.kind)?.minutes ?? 0;
	}

	// Une colonne par palier RÉELLEMENT applicable — taux ET nature. Les paliers d'un temps partiel
	// se calculent depuis chaque contrat : ils n'existent pas dans les règles de l'établissement,
	// mais doivent avoir leur colonne (cf. payrollTiersOf).
	get tiers(): PayrollTierColumn[] {
		return payrollTiersOf(this.rows, this.rules);
	}

	// Colspan du groupe « Heures supplémentaires » : les paliers, plus « Payées », plus les colonnes
	// de conformité quand elles ont quelque chose à dire. Sans ce calcul, la ligne de groupes est
	// plus courte que la ligne de colonnes et TOUT le tableau se décale vers la gauche — « Payées »
	// et « Contingent » se retrouvent alors annoncés sous « Absences ».
	// Contrat et Réalisé, toujours ; Planifié et Badgé seulement quand ils ont quelque chose à dire.
	get periodColspan(): number {
		return 2 + (this.showBadged ? 1 : 0) + (this.showPlanned ? 1 : 0);
	}

	get overtimeColspan(): number {
		return this.tiers.length + 1 + (this.showRestEdit ? 1 : 0) + (this.showContingent ? 1 : 0);
	}

	// UNE COLONNE PAR TAUX RÉELLEMENT APPLIQUÉ, pas une par règle : un mois qui contient le 1ᵉʳ mai
	// (100 %, imposé par L3133-6) et le 8 mai (ce que dit l'accord) en produit deux. Un total à un
	// taux moyen ne se saisit dans aucun logiciel de paie.
	get premiumCols(): PayrollPremiumColumn[] {
		return payrollPremiumsOf(this.rows, this.rules);
	}

	premiumMinutes(row: PayrollRow, col: PayrollPremiumColumn): number {
		return payrollPremiumMinutes(row, col);
	}

	// Le repos que cette colonne ouvre pour cette ligne — « 7 h travaillées le 1ᵉʳ mai, 7 h à
	// récupérer ». Zéro quand la majoration se paie et rien de plus.
	premiumRest(row: PayrollRow, col: PayrollPremiumColumn): number {
		return Math.round(this.premiumMinutes(row, col) * col.rest_rate / 100);
	}

	get premiumColspan(): number {
		return this.premiumCols.length;
	}

	// Le groupe « Absences » couvre les colonnes de types, leur total, et la retenue. Une absence
	// saisie EN HEURES ne décompte aucun jour : elle peut donc créer une retenue sans qu'aucun type
	// n'ait de colonne. D'où les deux termes indépendants — sinon l'en-tête se désaligne à nouveau.
	get absenceColspan(): number {
		return (this.usedAbsenceTypes.length > 0 ? this.usedAbsenceTypes.length + 1 : 0) + (this.showRetenue ? 1 : 0);
	}

	absenceDaysOf(row: PayrollRow, typ: number): number {
		return row.absences.find(a => a.typ === typ)?.days ?? 0;
	}

	// Types réellement rencontrés sur la période : inutile d'afficher 12 colonnes vides.
	get usedAbsenceTypes(): C_value[] {
		return this.absenceTypes.filter(t => this.rows.some(r => r.absences.some(a => a.typ === t.id && a.days > 0)));
	}

	// ============================================================
	// Absences : ce que la loi en fait
	// ============================================================

	// La colonne « À retenir » n'apparaît que s'il y a effectivement des heures non rémunérées :
	// une équipe sans congé sans solde ni grève n'a pas besoin d'une colonne de zéros.
	get showRetenue(): boolean {
		return this.rows.some(r => r.absence_summary.unpaid_min > 0);
	}

	// Le tableau du bas : une ligne par salarié × type d'absence, avec le traitement de paie.
	// C'est la réponse à « 3 jours d'absence, d'accord, mais j'en fais quoi ? ».
	get absenceRows(): { name: string; absence: AbsenceTotal }[] {
		return this.rows.flatMap(r => r.absences
			.filter(a => a.days > 0 || a.minutes > 0)
			.map(a => ({ name: r.name, absence: a })));
	}

	// Les types utilisés que le catalogue n'a pas su rattacher : leur traitement en paie est une
	// hypothèse, pas une règle. Le dire vaut mieux que de laisser croire à une qualification.
	get unqualifiedTypes(): string[] {
		return Array.from(new Set(this.rows.flatMap(r => r.absence_summary.unqualified))).sort();
	}

	payLabel(a: AbsenceTotal): string { return hrAbsencePayLabel(a.profile.pay); }
	workTimeLabel(a: AbsenceTotal): string { return hrAbsenceWorkTimeLabel(a.profile.work_time); }
	// Le rythme d'acquisition est celui de L'ÉTABLISSEMENT (2,5 j/mois en jours ouvrables, 2,08 en
	// jours ouvrés) : ce tableau annonce ce que le compteur applique réellement, pas la règle
	// générale du Code du travail.
	accrualLabel(a: AbsenceTotal): string { return hrAbsenceAccrualLabel(a.profile.leave_accrual, this.leaveRules); }
	profileLabel(a: AbsenceTotal): string { return hrAbsenceProfileLabel(a.profile, this.leaveRules); }

	// Classe de pastille : ce qui coûte à l'employeur, ce que la Sécu prend en charge, ce qui se retient.
	payClass(a: AbsenceTotal): string {
		switch (a.profile.pay) {
			case 'employer': return 'pay-employer';
			case 'social': return 'pay-social';
			case 'partial': return 'pay-partial';
			default: return 'pay-unpaid';
		}
	}

	// L'infobulle d'un en-tête de type : tout le régime légal en une phrase.
	absenceTypeTooltip(t: C_value): string {
		const a = this.rows.flatMap(r => r.absences).find(x => x.typ === t.id);
		return a ? hrAbsenceProfileLabel(a.profile, this.leaveRules) : (t.name ?? '');
	}

	get incompleteCount(): number {
		return this.rows.filter(r => r.incomplete.length > 0).length;
	}

	incompleteTooltip(row: PayrollRow): string {
		return 'Manque pour la paie : ' + row.incomplete.join(', ');
	}

	// Totaux de bas de tableau — ce que le gérant regarde en premier.
	get totalPaid(): number { return this.rows.reduce((s, r) => s + r.paid_min, 0); }
	get totalOvertime(): number { return this.rows.reduce((s, r) => s + r.overtime_min, 0); }
	get totalRest(): number { return this.rows.reduce((s, r) => s + this.restCreditedMinutes(r), 0); }
	get totalRetained(): number { return this.rows.reduce((s, r) => s + this.payableMinutes(r), 0); }

	// ============================================================
	// Arbitrage des heures majorées — le manager déplace, le total suit
	// ============================================================
	//
	// Le moteur ventile les heures dans les paliers que la LOI impose (+25 %, +50 %, heures
	// complémentaires…). Le manager garde la main : il peut sortir des heures d'un taux pour les
	// poser sur un autre — typiquement vers « Non majoré » quand elles ont déjà été compensées.
	// « Heures sup payées » n'est plus une saisie indépendante mais la SOMME de ces colonnes : un
	// total ne doit jamais pouvoir contredire son propre détail.

	// Les lundis que cette paie compte — l'axe de la vue par semaine, et la maille de l'arbitrage.
	get countedMondays(): string[] {
		return retainedMondays(this.weekChoices);
	}

	// Une ligne par semaine COMPTÉE, même celles où le salarié n'a rien fait : c'est une semaine
	// de la paie, on doit pouvoir y poser des heures — et voir qu'elle est vide.
	weekLinesOf(row: PayrollRow): PayrollWeekLine[] {
		return this.countedMondays.map(monday => row.weeks.find(w => w.monday === monday)
			?? { monday, contract_min: row.contract_week_min, worked_min: 0, basis_min: 0, overtime: [] });
	}

	// « S28 (06/07 → 12/07) » — le langage de l'utilisateur, déjà construit par le moteur.
	weekLabelOf(monday: string): string {
		return this.weekChoices.find(w => w.monday === monday)?.label ?? monday;
	}

	// UNE SEMAINE À CHEVAL sur le mois. Le contrat la compte ENTIÈRE (35h × 5), mais les heures ne
	// sont lues que sur les jours de la période : la semaine paraît alors déficitaire alors qu'elle
	// a été travaillée normalement. Tant que le rapport lit les heures au mois et le contrat à la
	// semaine, l'écart doit se VOIR plutôt que se deviner.
	isPartialWeek(monday: string): boolean {
		return monday < this.from || addDays(monday, 6) > this.to;
	}

	partialWeekTooltip(monday: string): string {
		return 'Semaine à cheval sur le mois : le contrat la compte entière, mais seules les heures '
			+ 'du ' + hrPeriodLabel(monday < this.from ? this.from : monday,
				addDays(monday, 6) > this.to ? this.to : addDays(monday, 6))
			+ ' sont lues ici. L\'écart au contrat de cette ligne n\'est donc pas un déficit réel.';
	}

	// Les colonnes qui suivent « Heures sup payées » : une ligne de semaine ne les remplit pas
	// (absences, titres-restaurant, commentaire se lisent au mois), mais doit les couvrir.
	get weekTailColspan(): number {
		return (this.showRestEdit ? 1 : 0) + (this.showContingent ? 1 : 0)
			+ this.premiumColspan + this.absenceColspan + (this.showVouchers ? 1 : 0) + 1;
	}

	// Les paliers SAISISSABLES : ceux qui se paient. RTT, récupération et équivalence sont des
	// régimes de compensation, pas des lignes de bulletin — ils restent en lecture seule.
	get payableTiers(): PayrollTierColumn[] {
		return this.tiers.filter(t => t.kind === 'paid');
	}

	// Ce que le MOTEUR propose, pour une semaine ou pour le mois entier.
	private engineMinutes(row: PayrollRow, tier: PayrollTierColumn, monday?: string): number {
		if (monday === undefined) return this.overtimeAt(row, tier);
		return row.entries
			.filter(e => e.monday === monday && e.rate === tier.rate && (e.tier_kind ?? 'paid') === tier.kind)
			.reduce((sum, e) => sum + e.minutes, 0);
	}

	// La valeur RETENUE : la décision du manager si elle existe pour cette semaine, la proposition
	// du moteur sinon. Sur le mois, c'est la somme des semaines comptées — arbitrer une seule
	// semaine ne doit pas effacer ce que le moteur dit des autres.
	tierMinutes(row: PayrollRow, tier: PayrollTierColumn, monday?: string): number {
		if (monday === undefined) {
			return this.countedMondays.reduce((sum, m) => sum + this.tierMinutes(row, tier, m), 0);
		}
		const decided = arbitratedMinutes(this.arbitration, row.id_employee, monday, payrollTierKey(tier.rate, tier.kind));
		return decided ?? this.engineMinutes(row, tier, monday);
	}

	// Le total payable de la ligne : la somme des colonnes saisissables, MOINS ce qui part en
	// récupération. C'est ce chiffre qui part au comptable — une heure récupérée n'est pas payée,
	// c'est toute la définition du repos compensateur de remplacement (L3121-33).
	payableMinutes(row: PayrollRow, monday?: string): number {
		return this.payableTiers.reduce(
			(sum, t) => sum + Math.max(0, this.tierMinutes(row, t, monday) - this.restMinutes(row, t, monday)), 0);
	}

	// Une saisie par (salarié, semaine ou mois, palier). Créée à la demande et gardée : la
	// recréer à chaque cycle de détection ferait perdre le focus à chaque frappe.
	tierCtrl(row: PayrollRow, tier: PayrollTierColumn, monday?: string): FormControl<number | null> {
		const key = row.id_employee + '|' + (monday ?? 'M') + '|' + payrollTierKey(tier.rate, tier.kind);
		let ctrl = this.tierCtrls.get(key);
		if (!ctrl) {
			ctrl = new FormControl<number | null>(Math.round((this.tierMinutes(row, tier, monday) / 60) * 100) / 100);
			this.tierCtrls.set(key, ctrl);
		}
		return ctrl;
	}

	// Enregistre une colonne. Depuis la vue par semaine la décision se pose telle quelle ; depuis
	// le récapitulatif elle se répartit sur les semaines au prorata de ce qu'elles portent déjà —
	// l'arbitrage vit toujours à la semaine, c'est là que naissent les heures supplémentaires.
	onSaveTier(row: PayrollRow, tier: PayrollTierColumn, monday?: string): void {
		if (!this.isAdmin || this.savingWeeks) return;
		const hours = this.tierCtrl(row, tier, monday).value;
		if (hours === null || isNaN(+hours) || +hours < 0) {
			this.alertService.error('Indiquez un nombre d\'heures (0 pour ne rien payer à ce taux).');
			return;
		}
		const minutes = Math.round(+hours * 60);
		const key = payrollTierKey(tier.rate, tier.kind);
		const byWeek = monday !== undefined
			? { [monday]: minutes }
			: spreadOvertime(
				this.countedMondays.map(m => ({ monday: m, minutes: this.tierMinutes(row, tier, m) })),
				minutes);

		this.arbitration = withArbitration(this.arbitration, row.id_employee, key, byWeek);
		this.writeClosure(this.closureFor(this.storeCtrl.value)?.closed_at ?? null, this.arbitration,
			'Heures « ' + tier.label + ' » enregistrées pour ' + row.name,
			this.syncPaidCounter$(row));
	}

	// Revenir à la proposition du moteur : on retire la décision, on ne la remet pas à zéro —
	// « zéro heure payée » et « laisse le calcul décider » sont deux réponses différentes.
	onResetTiers(row: PayrollRow): void {
		if (!this.isAdmin || this.savingWeeks) return;
		this.arbitration = withoutEmployee(this.arbitration, row.id_employee);
		this.restArbitration = withoutEmployee(this.restArbitration, row.id_employee);
		this.writeClosure(this.closureFor(this.storeCtrl.value)?.closed_at ?? null, this.arbitration,
			'Répartition automatique rétablie pour ' + row.name,
			this.syncPaidCounter$(row));
	}

	// ============================================================
	// METTRE DES HEURES EN RÉCUPÉRATION
	// ============================================================
	//
	// « Ces 4 h, je ne les paie pas — il les récupère. » Le geste est le même que l'arbitrage des
	// taux : on saisit des heures, on valide. Ce qui change, c'est leur destination : elles sortent
	// du montant à payer et entrent dans le compteur d'heures à récupérer du salarié, MAJORATION
	// COMPRISE (L3121-33) — 4 h à +25 % font 5 h de repos, pas 4.
	//
	// La saisie porte sur le TOTAL DU MOIS, ventilé sur les paliers du plus majoré au moins majoré :
	// c'est ce que veut celui qui récupère (il commence par les heures qui rapportent le plus de
	// repos), et personne ne demande à choisir « 2 h de +50 % et 1 h de +25 % ».

	// Ce qui est décidé pour cette semaine et ce palier — `null` si personne n'a rien décidé.
	private restDecided(row: PayrollRow, tier: PayrollTierColumn, monday: string): number | null {
		return arbitratedMinutes(this.restArbitration, row.id_employee, monday, payrollTierKey(tier.rate, tier.kind));
	}

	// Les heures effectivement mises en repos sur ce palier — la décision si elle existe, sinon ce
	// que le moteur a proposé au titre de la politique de l'établissement.
	restMinutes(row: PayrollRow, tier: PayrollTierColumn, monday?: string): number {
		if (monday === undefined) {
			return this.countedMondays.reduce((sum, m) => sum + this.restMinutes(row, tier, m), 0);
		}
		const decided = this.restDecided(row, tier, monday);
		const proposed = row.entries
			.filter(e => e.monday === monday && e.rate === tier.rate && (e.tier_kind ?? 'paid') === tier.kind
				&& e.destination === 'rest')
			.reduce((sum, e) => sum + e.minutes, 0);
		// Jamais plus que ce que le palier porte : le manager peut avoir déplacé des heures d'un
		// taux à l'autre APRÈS avoir décidé d'en récupérer, et une récupération plus grande que sa
		// colonne rendrait un montant à payer négatif.
		return Math.min(decided ?? proposed, this.tierMinutes(row, tier, monday));
	}

	// Le total du mois mis en récupération, tous paliers confondus.
	restTotalMinutes(row: PayrollRow, monday?: string): number {
		return this.payableTiers.reduce((sum, t) => sum + this.restMinutes(row, t, monday), 0);
	}

	// … et ce que ça VAUT en repos, majoration comprise. C'est ce chiffre que le salarié posera.
	restCreditedMinutes(row: PayrollRow, monday?: string): number {
		return this.payableTiers.reduce(
			(sum, t) => sum + Math.round(this.restMinutes(row, t, monday) * (1 + t.rate / 100)), 0);
	}

	restCtrl(row: PayrollRow, monday?: string): FormControl<number | null> {
		const key = row.id_employee + '|' + (monday ?? 'M');
		let ctrl = this.restCtrls.get(key);
		if (!ctrl) {
			ctrl = new FormControl<number | null>(Math.round((this.restTotalMinutes(row, monday) / 60) * 100) / 100);
			this.restCtrls.set(key, ctrl);
		}
		return ctrl;
	}

	// Enregistre « N heures en récupération » pour un salarié. La ventilation part des paliers les
	// PLUS MAJORÉS : ce sont eux qui rendent le plus de repos, et c'est ce que choisirait n'importe
	// qui à qui on poserait la question.
	onSaveRest(row: PayrollRow, monday?: string): void {
		if (!this.isAdmin || this.savingWeeks) return;
		const hours = this.restCtrl(row, monday).value;
		if (hours === null || isNaN(+hours) || +hours < 0) {
			this.alertService.error('Indiquez un nombre d\'heures à récupérer (0 pour tout payer).');
			return;
		}
		const asked = Math.round(+hours * 60);
		const mondays = monday !== undefined ? [monday] : this.countedMondays;
		// Le plafond est le TOTAL des heures majorées, pas ce qui reste à payer : la saisie remplace
		// la décision précédente, elle ne s'y ajoute pas. Sans cette nuance, passer de 6 h à 8 h de
		// récupération serait refusé au motif qu'il n'en resterait que 14 sur 20.
		const available = mondays.reduce(
			(sum, m) => sum + this.payableTiers.reduce((s, t) => s + this.tierMinutes(row, t, m), 0), 0);
		if (asked > available) {
			this.alertService.error('Seulement ' + hoursLabel(available) + ' d\'heures majorées sur la période : '
				+ 'impossible d\'en mettre ' + hoursLabel(asked) + ' en récupération.');
			return;
		}

		// Du palier le plus majoré au moins majoré, semaine par semaine.
		let left = asked;
		let next = this.restArbitration;
		const tiers = [...this.payableTiers].sort((a, b) => b.rate - a.rate);
		for (const t of tiers) {
			const byWeek: { [monday: string]: number } = {};
			for (const m of mondays) {
				const take = Math.min(left, this.tierMinutes(row, t, m));
				byWeek[m] = take;
				left -= take;
			}
			next = withArbitration(next, row.id_employee, payrollTierKey(t.rate, t.kind), byWeek);
		}

		this.restArbitration = next;
		this.writeClosure(this.closureFor(this.storeCtrl.value)?.closed_at ?? null, this.arbitration,
			asked > 0
				? hoursLabel(asked) + ' mises en récupération pour ' + row.name
					+ ' — ' + rcrLabel(this.creditedFor(row, mondays, asked)) + ' de repos acquis'
				: 'Heures de ' + row.name + ' à nouveau toutes payées',
			this.syncPaidCounter$(row));
	}

	// L'équivalent en repos de `asked` minutes réparties comme `onSaveRest` vient de le faire —
	// calculé sur la même ventilation, pour que le message dise exactement ce qui a été enregistré.
	private creditedFor(row: PayrollRow, mondays: string[], asked: number): number {
		let left = asked, credited = 0;
		for (const t of [...this.payableTiers].sort((a, b) => b.rate - a.rate)) {
			for (const m of mondays) {
				const take = Math.min(left, this.tierMinutes(row, t, m));
				credited += Math.round(take * (1 + t.rate / 100));
				left -= take;
			}
		}
		return credited;
	}

	// La ligne porte-t-elle une mise en récupération décidée à la main ?
	isRestArbitrated(row: PayrollRow): boolean {
		const byWeek = this.restArbitration[String(row.id_employee)];
		return !!byWeek && Object.keys(byWeek).length > 0;
	}

	// L'infobulle du stock à récupérer : d'où viennent ces heures. Un chiffre qui additionne trois
	// régimes légaux doit pouvoir se décomposer, sinon personne ne peut le vérifier.
	restTooltip(row: PayrollRow): string {
		const bits: string[] = [];
		if (row.rcr_credited_min > 0) {
			bits.push(hoursLabel(row.rcr_credited_min) + ' d\'heures supplémentaires récupérées (majoration comprise)');
		}
		if (row.premium_rest_min > 0) {
			bits.push(hoursLabel(row.premium_rest_min) + ' au titre des majorations (dimanche, jours fériés)');
		}
		if (row.contingent.cor_balance_min > 0) {
			bits.push(hoursLabel(row.contingent.cor_balance_min) + ' de contrepartie obligatoire en repos '
				+ '(au-delà du contingent annuel, à prendre sous 2 mois)');
		}
		return bits.length ? bits.join('\n') : 'Aucune heure à récupérer sur cette période';
	}

	// Contrepartie obligatoire en repos DÉJÀ PRISE, par salarié : les absences d'un type marqué
	// « COR » dans le référentiel, valorisées au planning qu'elles effacent.
	private corTakenOf(absences: HrAbsence[], slots: EffectiveSlot[]): Map<number, number> {
		const types = corAbsenceTypes(this.absenceTypes);
		const out = new Map<number, number>();
		if (!types.length) return out;
		for (const a of absences) {
			if (a.typ == null || !types.includes(a.typ) || a.state !== HR_ABSENCE_STATE_APPROVED) continue;
			const cost = rcrCostOfRequest(a, slots.filter(s => s.id_employee === a.id_employee));
			out.set(a.id_employee, (out.get(a.id_employee) ?? 0) + cost);
		}
		return out;
	}

	// LE MIROIR COMPTABLE de l'arbitrage. Les heures payées SORTENT du stock récupérable : sans ce
	// mouvement (hr_counter kind 3), le compteur du salarié continuerait d'afficher des heures
	// déjà versées sur son bulletin. Un seul mouvement par salarié et par période : on remplace le
	// précédent plutôt que d'empiler des corrections.
	private syncPaidCounter$(row: PayrollRow): Observable<any> {
		const minutes = -this.payableMinutes(row);
		const existing = this.paidAdjust.get(row.id_employee);
		const drop$ = existing
			? this.hrService.postHrEvent(EVENTTYP_DELETE, OBJECTTYP_HR_COUNTER, { id: existing.id })
			: of(null as any);
		return drop$.pipe(switchMap(() => minutes === 0
			? of(null as any)
			: this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_COUNTER, {
				id: 0, id_employee: row.id_employee, dat: this.to, minutes,
				kind: HR_COUNTER_KIND_PAID, comment: 'Heures majorées payées — paie ' + this.from.slice(0, 7),
			})));
	}

	// La ligne porte-t-elle une décision humaine ? L'écran doit le dire : sinon on ne sait plus si
	// un chiffre vient de la loi ou de quelqu'un.
	isArbitrated(row: PayrollRow): boolean {
		const byWeek = this.arbitration[String(row.id_employee)];
		return !!byWeek && Object.keys(byWeek).length > 0;
	}

	// Alertes légales de la ligne (temps partiel, contingent, plafonds) — infobulle du triangle.
	legalTooltip(row: PayrollRow): string {
		return row.issues.map(i => i.detail).join('\n');
	}
	get totalAbsenceDays(): number { return this.rows.reduce((s, r) => s + r.absence_days, 0); }
	get totalRetenue(): number { return this.rows.reduce((s, r) => s + r.absence_summary.unpaid_min, 0); }
	get totalVouchers(): number { return this.rows.reduce((s, r) => s + r.meal_vouchers, 0); }
	// La RETENUE totale des titres — la part salariale, celle qui part sur les bulletins.
	get totalVoucherRetenue(): number { return round2(this.rows.reduce((s, r) => s + r.meal_employee_amount, 0)); }
	euros(n: number): string { return euros(n); }

	// ============================================================
	// Export
	// ============================================================

	onExportCsv(): void {
		// L'export porte les chiffres DE L'ÉCRAN, arbitrage compris : deux totaux différents entre
		// le rapport et le CSV feraient douter des deux.
		const arbitrated = new Map<string, number>();
		for (const row of this.rows) {
			for (const t of this.tiers) {
				// Les heures mises en récupération ne sont pas payées : la colonne du CSV doit dire
				// ce que le comptable règle, pas ce que le salarié a fait.
				arbitrated.set(row.id_employee + '|' + t.rate + '|' + t.kind,
					Math.max(0, this.tierMinutes(row, t) - this.restMinutes(row, t)));
			}
		}
		const csv = buildPayrollCsv(this.rows, this.rules, this.usedAbsenceTypes, this.from, this.to,
			new Map(this.rows.map(r => [r.id_employee, this.payableMinutes(r)])),
			this.weeksLabel, arbitrated);
		const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });		// BOM : accents OK dans Excel
		const a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = 'paie_' + this.from.slice(0, 7) + '.csv';
		a.click();
		URL.revokeObjectURL(a.href);
	}
}
